import find from 'lodash/find';
import get from 'lodash/get';
import React, { FC, useCallback } from 'react';
import styled from 'styled-components';

import ConditionRow from './ConditionRow';
import {
  CurrencyType,
  CustomAttributeCurrencyCondition as CustomAttributeCurrencyConditionType,
} from 'src/actions/rules/condition';
import NumericConditionSection from '../shared/NumericConditionSection';
import { StyledConditionLabel, StyledDropdown } from '../shared/StyledComponents';
import { Tooltip } from 'src/presentation/components/agent_desktop_components';
import useCustomAttributes from '../hooks/useCustomAttributes';

type CustomAttributeCurrencyConditionProps = {
  condition: CustomAttributeCurrencyConditionType;
  index: number;
  isReadOnly?: boolean;
  onDelete: Function;
  onUpdate: Function;
};

const CustomAttributeCurrencyCondition: FC<CustomAttributeCurrencyConditionProps> = ({
  condition,
  index,
  isReadOnly,
  onDelete,
  onUpdate,
}) => {
  const onCurrencyChange = useCallback(
    val => {
      const doesCurrentCurrencyConvert = nonConversionCurrencies.indexOf(condition.currency) === -1;
      const doesNewCurrencyConvert = nonConversionCurrencies.indexOf(val) === -1;

      if (doesCurrentCurrencyConvert && !doesNewCurrencyConvert) {
        condition.setValue(Math.floor(condition.value / 100));
        condition.setBegin(Math.floor(condition.begin / 100));
        condition.setEnd(Math.floor(condition.end / 100));
      }

      if (!doesCurrentCurrencyConvert && doesNewCurrencyConvert) {
        condition.setValue(Math.floor(condition.value * 100));
        condition.setBegin(Math.floor(condition.begin * 100));
        condition.setEnd(Math.floor(condition.end * 100));
      }

      condition.setCurrency(val);
      onUpdate({ condition, index });
    },
    [condition, index, onUpdate]
  );

  const convertToDisplayFn = val =>
    nonConversionCurrencies.indexOf(condition.currency) === -1 ? Number(val) / 100 : Number(val);
  const convertFromDisplayFn = val =>
    nonConversionCurrencies.indexOf(condition.currency) === -1 ? Math.ceil(Number(val) * 100) : Number(val);

  const limitDecimals = nonConversionCurrencies.indexOf(condition.currency) === -1 ? 2 : 0;

  const label = get(find(useCustomAttributes(), { attr: condition.name }), 'label');

  if (isReadOnly) {
    return (
      <>
        <StyledRow index={index} isReadOnly onDelete={onDelete}>
          <SelectorRow>
            <StyledConditionLabel disabled data-aid="readOnlyCurrencyConditionLabel">
              {label}
            </StyledConditionLabel>
            <NumericConditionSection
              isReadOnly
              convertToDisplayFn={convertToDisplayFn}
              convertFromDisplayFn={convertFromDisplayFn}
              condition={condition}
              index={index}
              limitDecimals={limitDecimals}
              onUpdate={onUpdate}
            />
            <span data-aid="readOnlyCurrencyTypeLabel">{condition.currency}</span>
          </SelectorRow>
        </StyledRow>
      </>
    );
  }

  const currencyOptions = Object.keys(CurrencyType).map(key => {
    return { value: key, label: CurrencyType[key] };
  });

  return (
    <StyledRow index={index} onDelete={onDelete}>
      <Wrapper>
        <SelectorRow>
          <Tooltip message={label}>
            <ConditionLabel disabled data-aid="currencyConditionLabel">
              {label}
            </ConditionLabel>
          </Tooltip>
          <NumericConditionSection
            convertToDisplayFn={convertToDisplayFn}
            convertFromDisplayFn={convertFromDisplayFn}
            condition={condition}
            index={index}
            limitDecimals={limitDecimals}
            onUpdate={onUpdate}
          />
        </SelectorRow>
        <StyledDropdown
          data-aid="currencyConditionCurrencyDropdown"
          searchable
          onSelect={onCurrencyChange}
          options={currencyOptions}
          value={condition.currency}
          placeholder="Select currency type"
        />
      </Wrapper>
    </StyledRow>
  );
};

const nonConversionCurrencies = [CurrencyType.JPY, CurrencyType.KRW];

const ConditionLabel = styled(StyledConditionLabel)`
  margin-right: 6px;
  max-width: 160px;
`;

const SelectorRow = styled.div`
  align-items: center;
  display: flex;
  padding-bottom: 12px;
`;

const StyledRow = styled(ConditionRow)`
  align-items: baseline;
`;

const Wrapper = styled.div`
  width: 100%;
`;

export default CustomAttributeCurrencyCondition;
